// File: src/pages/OnboardingPage.js
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useProfile } from '../context/ProfileContext';
import { getFirestore, doc, updateDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';
import TermsOfService202611 from '../components/miscellaneous/TermsOfService202611';

const OnboardingPage = () => {
  const { user, userProfile, isDarkMode } = useProfile();
  const navigate = useNavigate();
  const db = getFirestore();
  const scrollRef = useRef(null);

  // Layout Stepper Management
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [canProceedFromStep4, setCanProceedFromStep4] = useState(false);

  // Parameter State Handlers
  const [tosAgreed, setTosAgreed] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [customName, setCustomName] = useState(userProfile?.displayName || 'Anonymous');
  const [selectedState, setSelectedState] = useState('');
  const [suburbSearchQuery, setSuburbSearchQuery] = useState('');
  const [allSuburbsInState, setAllSuburbsInState] = useState([]);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [selectedSuburb, setSelectedSuburb] = useState(null);
  const [isLoadingSuburbs, setIsLoadingSuburbs] = useState(false);

  const [preferNotToSay, setPreferNotToSay] = useState(false);
  const [isStateDropdownOpen, setIsStateDropdownOpen] = useState(false);

  // Download regional data points asynchronously 
  useEffect(() => {
    if (!selectedState || selectedState === 'INT') {
      setAllSuburbsInState([]);
      return;
    }

    const fetchStateSuburbs = async () => {
      setIsLoadingSuburbs(true);
      setSelectedSuburb(null);
      setSuburbSearchQuery('');
      try {
        const response = await fetch(
          `https://cdn.jsdelivr.net/gh/schappim/australian-postcodes@master/data/by-state/${selectedState}.csv`
        );
        const csvText = await response.text();
        const lines = csvText.split('\n');
        const parsed = lines.slice(1).map(line => {
          const cols = line.split(',');
          return { suburb: cols[1]?.trim(), state: cols[2]?.trim() };
        }).filter(item => item.suburb);

        const unique = Array.from(new Map(parsed.map(item => [item.suburb, item])).values());
        setAllSuburbsInState(unique);
      } catch (err) {
        console.error("ONBOARDING: Suburb sync error", err);
      } finally {
        setIsLoadingSuburbs(false);
      }
    };
    fetchStateSuburbs();
  }, [selectedState]);

  // Autocomplete suggestions filtering
  useEffect(() => {
    if (!suburbSearchQuery.trim()) {
      setFilteredSuggestions([]);
      return;
    }
    const query = suburbSearchQuery.toLowerCase();
    const matches = allSuburbsInState
      .filter(item => item.suburb.toLowerCase().startsWith(query))
      .slice(0, 6);
    setFilteredSuggestions(matches);
  }, [suburbSearchQuery, allSuburbsInState]);

  // Detect scroll boundary metrics for compliance logic
  const handleScrollToBottomCheck = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 24) {
      setHasScrolledToBottom(true);
    }
  };

  const commitCoreActivationRecords = async () => {
    if (!user?.uid) return false;
    try {
      const userRef = doc(db, "users", user.uid);
      
      await updateDoc(userRef, {
        displayName: customName.trim() || 'Anonymous',
        displayName_lowercase: (customName.trim() || 'Anonymous').toLowerCase(),
        city: (preferNotToSay || selectedState === 'INT') ? "" : selectedSuburb?.suburb || "Undisclosed",
        state: preferNotToSay ? "Undisclosed" : selectedState || "Undisclosed",
        country: selectedState === 'INT' ? "International" : "Australia",
        isGhosted: false,
        termsAccepted: true,
        termsAcceptedAt: new Date().toISOString(),
        legalAgreementVersion: "2.3-NOVEMBER-2026",
        isOnboarded: false, 
        onboardingStep: 3
      });
      return true;
    } catch (err) {
      console.error("ONBOARDING: Execution error committing account settings:", err);
      toast.error("Failed to finalize profile registry items.");
      return false;
    }
  };

  // Forward Navigation Control Flow
  const handleNextStep = async () => {
    let nextStep = currentStep + 1;

    // STEP 2 VALIDATION CONTROL FLOW (Allows 'INT' without requiring a suburb)
    if (currentStep === 2) {
      if (!selectedState) {
        toast.error("Please pick a valid state or region to continue.");
        return;
      }
      if (selectedState !== 'INT' && !preferNotToSay && !selectedSuburb) {
        toast.error("Please select your local suburb from the dropdown or tick 'Prefer not to say'.");
        return;
      }
      nextStep = 3;
    }

    // STEP 3 VALIDATION CONTROL FLOW
    if (currentStep === 3) {
      if (!tosAgreed) {
        toast.error("You must explicitly verify and accept the platform agreements.");
        return;
      }
      setIsSubmitting(true);
      const success = await commitCoreActivationRecords();
      setIsSubmitting(false);
      if (!success) return; 
      nextStep = 4;
    }

    // STEP 4 BYPASS: Jump straight from Step 4 (Fans) to Step 7 (Finish)
    if (currentStep === 4) {
      nextStep = 7;
    }

    // SYNC STEP TO FIRESTORE
    if (user?.uid) {
      try {
        const userRef = doc(db, "users", user.uid);
        await updateDoc(userRef, { onboardingStep: nextStep });
      } catch (err) {
        console.error(`ONBOARDING: Failed to sync step ${nextStep} to database:`, err);
      }
    }

    setCurrentStep(nextStep);
  };

  const handleFinishWizard = async (targetRoute) => {
    if (!user?.uid) return;

    try {
      const userRef = doc(db, "users", user.uid);
      const finalUpdates = {
        isOnboarded: true,
        onboardingStep: 7
      };

      await updateDoc(userRef, finalUpdates);

      if (targetRoute === '/profile') {
        navigate('/profile', { state: { triggerArtistOverlay: true } });
      } else {
        toast.success("Welcome to Atomic!");
        navigate('/trends');
      }
    } catch (err) {
      console.error("ONBOARDING: Subscription or final allocation error:", err);
      toast.error("Something went wrong completing your profile.");
    }
  };

  useEffect(() => {
    if (!isStateDropdownOpen) return;
    const handleOutsideClickClose = () => setIsStateDropdownOpen(false);
    window.addEventListener('click', handleOutsideClickClose);
    return () => window.removeEventListener('click', handleOutsideClickClose);
  }, [isStateDropdownOpen]);

  const pageBgClass = isDarkMode 
    ? "from-purple-950 via-zinc-950 to-[#ff00cc]" 
    : "from-[#ff00cc] via-[#ff4da6] to-purple-600";

  const editorialInputStyle = "w-full bg-transparent border-b-2 border-white/60 focus:border-white text-white font-black text-2xl py-2 outline-none placeholder:text-white/40 transition-all text-center";

  return (
    <div className={`min-h-screen w-full flex flex-col justify-between p-6 md:p-12 font-sans overflow-x-hidden bg-gradient-to-br animate-aurora-glow ${pageBgClass} text-white relative ${isDarkMode ? 'dark' : ''}`}>
      <div className="full-page-mesh-aurora" />

      <header className="w-full max-w-3xl mx-auto flex justify-end items-center z-10" />

      {/* CORE CONTENT SLIDER */}
      <main className="w-full max-w-3xl mx-auto flex-grow flex flex-col justify-center py-6 z-10">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 15 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -15 }}
            transition={{ duration: 0.2 }}
            className="w-full space-y-6 flex flex-col items-center text-center"
          >
            {/* STEP 1: WELCOME & NAME */}
            {currentStep === 1 && (
              <div className="space-y-6 w-full flex flex-col items-center">
                <div className="space-y-3 w-full">
                  <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black italic uppercase tracking-tighter leading-none w-full text-center drop-shadow-lg">
                    Welcome to Atomic!
                  </h2>
                  <p className="text-xl sm:text-2xl md:text-3xl font-bold text-white/90 max-w-2xl mx-auto leading-normal tracking-wide">
                    Let&apos;s personalize your music streaming Experience.
                  </p>
                </div>

                <div className="space-y-3 w-full max-w-md pt-6 text-center">
                  <label className="text-xs font-black uppercase tracking-widest text-white/70 block">Your Name:</label>
                  <input 
                    type="text" 
                    placeholder="Anonymous"
                    value={customName === 'Anonymous' ? '' : customName}
                    onChange={(e) => setCustomName(e.target.value || 'Anonymous')}
                    onBlur={(e) => { if(!e.target.value) setCustomName('Anonymous') }}
                    className={editorialInputStyle} 
                  />
                </div>
              </div>
            )}

            {/* STEP 2: LOCATION */}
            {currentStep === 2 && (
              <div className="w-full max-w-xl mx-auto flex flex-col justify-start h-full pt-4 space-y-12">
                <div className="space-y-3 w-full text-center mb-6">
                  <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black italic uppercase tracking-tighter leading-none w-full drop-shadow-lg">
                    Your location
                  </h2>
                </div>
                
                <div className="w-full max-w-md mx-auto space-y-10 text-left">
                  <div className="w-full relative">
                    <label className="text-xs font-black uppercase tracking-widest text-white/70 block mb-1 text-center">
                      1. Enter Your State:
                    </label>
                    <div 
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!preferNotToSay) setIsStateDropdownOpen(!isStateDropdownOpen);
                      }}
                      className="w-full border-b-2 border-white/60 focus:border-white text-white font-black text-xl py-2 cursor-pointer text-center relative flex justify-center items-center select-none"
                    >
                      <span>
                        {selectedState ? (
                          {
                            'NSW': 'New South Wales (NSW)',
                            'VIC': 'Victoria (VIC)',
                            'QLD': 'Queensland (QLD)',
                            'WA': 'Western Australia (WA)',
                            'SA': 'South Australia (SA)',
                            'TAS': 'Tasmania (TAS)',
                            'ACT': 'Australian Capital Territory (ACT)',
                            'NT': 'Northern Territory (NT)',
                            'INT': 'Outside Australia (International Profile)'
                          }[selectedState]
                        ) : (
                          <span className="opacity-100">Select Region...</span>
                        )}
                      </span>
                      <span className="absolute right-2 text-xs opacity-80 transition-transform duration-200" style={{ transform: isStateDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                        ▼
                      </span>
                    </div>

                    <AnimatePresence>
                      {isStateDropdownOpen && !preferNotToSay && (
                        <motion.div 
                          initial={{ opacity: 0, y: 5 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 5 }}
                          className="absolute left-0 right-0 mt-2 rounded-2xl shadow-2xl z-50 max-h-none overflow-y-auto bg-white text-slate-900 p-2 border border-slate-150"
                        >
                          {[
                            { code: 'NSW', label: 'New South Wales (NSW)' },
                            { code: 'VIC', label: 'Victoria (VIC)' },
                            { code: 'QLD', label: 'Queensland (QLD)' },
                            { code: 'WA', label: 'Western Australia (WA)' },
                            { code: 'SA', label: 'South Australia (SA)' },
                            { code: 'TAS', label: 'Tasmania (TAS)' },
                            { code: 'ACT', label: 'Australian Capital Territory (ACT)' },
                            { code: 'NT', label: 'Northern Territory (NT)' },
                            { code: 'INT', label: 'Outside Australia (International Profile)' }
                          ].map((region) => (
                            <button
                              key={region.code}
                              type="button"
                              onClick={() => {
                                setSelectedState(region.code);
                                setIsStateDropdownOpen(false);
                                setPreferNotToSay(false);
                                setSelectedSuburb(null);
                                setSuburbSearchQuery('');
                              }}
                              className={`w-full text-center px-4 py-2.5 text-xs font-black uppercase tracking-wider rounded-lg transition-colors border-b border-slate-100 last:border-none ${
                                selectedState === region.code 
                                  ? "bg-[#ff00cc] text-white" 
                                  : "text-slate-800 hover:bg-slate-100"
                              }`}
                            >
                              {region.label}
                            </button>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {selectedState && selectedState !== 'INT' && (
                    <motion.div 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="space-y-4 relative pt-4 w-full"
                    >
                      <div className={`transition-all duration-200 ${preferNotToSay ? "opacity-20 pointer-events-none scale-95" : "opacity-100"}`}>
                        <label className="text-xs font-black uppercase tracking-widest text-white/70 block mb-1 text-center">2. Search For Your Suburb:</label>
                        <input
                          type="text"
                          placeholder={isLoadingSuburbs ? "Downloading registry..." : "Type town or suburb..."}
                          disabled={isLoadingSuburbs || preferNotToSay}
                          value={selectedSuburb ? selectedSuburb.suburb : suburbSearchQuery}
                          onChange={(e) => { setSelectedSuburb(null); setSuburbSearchQuery(e.target.value); }}
                          className={editorialInputStyle}
                        />
                        {filteredSuggestions.length > 0 && !selectedSuburb && (
                          <div className="absolute left-0 right-0 mt-2 rounded-2xl shadow-2xl z-50 max-h-36 overflow-y-auto bg-white text-slate-900 p-2">
                            {filteredSuggestions.map((item, idx) => (
                              <button
                                key={idx}
                                onClick={() => { setSelectedSuburb(item); setSuburbSearchQuery(item.suburb); }}
                                className="w-full text-center px-4 py-2 text-xs font-black uppercase hover:bg-[#ff00cc] hover:text-white rounded-lg transition-colors"
                              >
                                {item.suburb}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="pt-2 w-full flex flex-col items-center space-y-4">
                        <label className="flex items-center space-x-3 p-3 bg-black/10 hover:bg-black/20 border border-white/10 rounded-xl select-none cursor-pointer transition-all">
                          <input 
                            type="checkbox" 
                            checked={preferNotToSay} 
                            onChange={(e) => {
                              setPreferNotToSay(e.target.checked);
                              if (e.target.checked) {
                                setSelectedSuburb(null);
                                setSuburbSearchQuery('');
                              }
                            }}
                            className="w-4 h-4 accent-white rounded bg-transparent border-white/40 cursor-pointer"
                          />
                          <span className="text-xs font-black uppercase tracking-wider text-white">Prefer not to say</span>
                        </label>

                        <AnimatePresence>
                          {preferNotToSay && (
                            <motion.div
                              initial={{ opacity: 0, height: 0, y: -5 }}
                              animate={{ opacity: 1, height: "auto", y: 0 }}
                              exit={{ opacity: 0, height: 0, y: -5 }}
                              className="w-full max-w-sm text-center overflow-hidden"
                            >
                              <div className="p-4 bg-black/20 backdrop-blur-sm border border-white/10 rounded-2xl shadow-xl mt-1">
                                <p className="text-xs font-bold text-[#FFFF00] leading-relaxed tracking-wide drop-shadow-sm">
                                  Knowing your location helps us connect you to the music and scene most relevant to you. Are you sure?
                                </p>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </motion.div>
                  )}
                </div>
              </div>
            )}

            {/* STEP 3: TERMS OF SERVICE */}
            {currentStep === 3 && (
              <div className="space-y-6 w-full max-w-xl flex flex-col items-center">
                <div className="space-y-2">
                  <h2 className="text-3xl sm:text-4xl md:text-5xl font-black italic uppercase tracking-tighter leading-none text-center drop-shadow-lg">
                    Terms of Service
                  </h2>
                  <p className="text-xl sm:text-2xl md:text-3xl font-bold text-white/90 max-w-2xl mx-auto leading-normal tracking-wide">
                    Please accept our Terms of Service to continue:
                  </p>
                </div>

                <div className="space-y-4 w-full max-w-lg flex flex-col items-center">
                  {/* High contrast, theme-aware terms card */}
                  <div 
                    ref={scrollRef}
                    onScroll={handleScrollToBottomCheck}
                    className={`h-72 w-full overflow-y-scroll p-6 text-[11px] text-left leading-relaxed rounded-2xl transition-all duration-300 scrollbar-thin shadow-2xl ${
                      isDarkMode 
                        ? "bg-zinc-950/90 border border-white/10 text-zinc-100 shadow-black/60" 
                        : "bg-white text-slate-900 border border-slate-200 shadow-2xl shadow-black/20"
                    }`}
                  >
                    <TermsOfService202611 />

                    <div className={`pt-6 pb-2 text-center font-black tracking-widest text-[9px] uppercase ${
                      isDarkMode ? "text-zinc-500" : "text-slate-400"
                    }`}>
                      --- End of Agreement ---
                    </div>
                  </div>
                  
                  {/* Agree Checkbox Card */}
                  <label className={`flex items-center justify-center space-x-3 p-3.5 rounded-2xl border select-none mx-auto w-fit transition-all duration-300 ${
                    isDarkMode 
                      ? "bg-black/30 border-white/15" 
                      : "bg-white/90 border-white text-slate-900 shadow-lg"
                  } ${!hasScrolledToBottom ? "opacity-40 cursor-not-allowed" : "cursor-pointer hover:scale-102"}`}>
                    <input 
                      type="checkbox" 
                      disabled={!hasScrolledToBottom} 
                      checked={tosAgreed} 
                      onChange={(e) => setTosAgreed(e.target.checked)} 
                      className={`w-5 h-5 rounded cursor-pointer disabled:cursor-not-allowed ${
                        isDarkMode ? "accent-[#ff00cc]" : "accent-[#ff00cc]"
                      }`}
                    />
                    <span className={`text-xs font-black uppercase tracking-wider ${
                      isDarkMode ? "text-white" : "text-slate-900"
                    }`}>
                      I Agree to the Terms of Service
                    </span>
                  </label>
                </div>
              </div>
            )}

            {/* STEP 4: FANS & POINTS ENGINE EXPLAINER */}
            {currentStep === 4 && (
              <motion.div 
                variants={{
                  hidden: { opacity: 1 },
                  visible: {
                    opacity: 1,
                    transition: {
                      delayChildren: 0.0,
                      staggerChildren: 2.2
                    }
                  }
                }}
                initial="hidden"
                animate="visible"
                className="space-y-8 max-w-2xl text-center w-full"
              >
                <motion.h2 
                  variants={{
                    hidden: { opacity: 0 },
                    visible: { opacity: 1, transition: { duration: 2 } }
                  }}
                  className="text-3xl sm:text-4xl font-black italic uppercase tracking-tight"
                >
                  How the points system works
                </motion.h2>

                <motion.ul className="space-y-6 text-xl sm:text-2xl md:text-3xl font-bold text-white/90 leading-normal tracking-wide text-center max-w-xl mx-auto">
                  <motion.li 
                    variants={{
                      hidden: { opacity: 0 },
                      visible: { opacity: 1, transition: { duration: 2.2 } }
                    }}
                  >
                    Rate Tracks to Earn App Points
                  </motion.li>

                  <motion.li 
                    variants={{
                      hidden: { opacity: 0 },
                      visible: { opacity: 1, transition: { duration: 2.2 } }
                    }}
                  >
                    Boost Songs to Drive Chart Ranks
                  </motion.li>

                  <motion.li 
                    variants={{
                      hidden: { opacity: 0 },
                      visible: { 
                        opacity: 1, 
                        transition: { 
                          duration: 2.2,
                          delayChildren: 1.8,
                          staggerChildren: 1 
                        } 
                      }
                    }}
                    className="space-y-2"
                  >
                    <div>Get Rewarded for Boosting:</div>
                    <div className="pl-0 mt-2 space-y-1 text-white/80 font-medium text-lg sm:text-xl md:text-2xl">
                      <motion.span 
                        variants={{ hidden: { opacity: 0 }, visible: { opacity: 1 } }} 
                        className="block"
                      >
                        Top Fan Profile Badges & Leaderboards
                      </motion.span>

                      <motion.span 
                        variants={{ hidden: { opacity: 0 }, visible: { opacity: 1 } }} 
                        className="block"
                      >
                        Unlock Offline Listening on Backed Tracks
                      </motion.span>
                      
                      <motion.span 
                        variants={{ hidden: { opacity: 0 }, visible: { opacity: 1 } }} 
                        className="block"
                      >
                        Exclusive Fan-Only Content Updates
                      </motion.span>
                      
                      <motion.span 
                        variants={{ 
                          hidden: { opacity: 0 }, 
                          visible: { opacity: 1, transition: { duration: 2.2 } } 
                        }} 
                        onAnimationComplete={() => setCanProceedFromStep4(true)}
                        className="block"
                      >
                        Community Chart Impact
                      </motion.span>
                    </div>
                  </motion.li>
                </motion.ul>
              </motion.div>
            )}

            {/* STEP 7: FINISH WIZARD & REDIRECT */}
            {currentStep === 7 && (
              <div className="space-y-6 w-full max-w-sm">
                <h2 className="text-4xl font-black italic uppercase tracking-tight leading-none">
                  Would you like to create an Artist profile?
                </h2>
                <div className="w-full space-y-3 pt-4">
                  <button
                    onClick={() => handleFinishWizard('/profile')}
                    className="w-full py-4 text-xs font-black uppercase tracking-widest bg-white text-slate-900 rounded-xl shadow-2xl hover:scale-102 transition-transform"
                  >
                    Absolutely!
                  </button>
                  <button
                    onClick={() => handleFinishWizard('/trends')}
                    className="w-full py-4 text-xs font-black uppercase tracking-widest bg-black/20 text-white border-2 border-white/20 hover:border-white rounded-xl transition-all"
                  >
                    No, take me to the app!
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* FOOTER NAVIGATION LAYER */}
      <footer className="w-full max-w-3xl mx-auto flex flex-col items-center gap-4 pt-4 border-t border-white/10 z-10">
        <div className="w-full flex items-center justify-center px-2 gap-4">

          {/* BACK BUTTON */}
          {currentStep > 1 && (
            <button
              onClick={() => {
                if (currentStep === 7) {
                  setCurrentStep(4);
                } else {
                  setCurrentStep(prev => prev - 1);
                }
              }}
              className="px-6 py-2.5 text-xs font-black uppercase tracking-widest text-white/70 hover:text-white border border-white/20 hover:border-white/60 rounded-xl transition-colors text-center shrink-0 min-w-[100px]"
            >
              Back
            </button>
          )}
          
          {/* CONTINUE BUTTON */}
          {currentStep < 7 && (
            <button
              onClick={handleNextStep}
              disabled={
                isSubmitting ||
                (currentStep === 2 && !preferNotToSay && !selectedState) ||
                (currentStep === 2 && !preferNotToSay && selectedState !== 'INT' && !selectedSuburb) ||
                (currentStep === 3 && !tosAgreed)
              }
              className="px-10 py-3 text-xs font-black uppercase tracking-widest bg-white text-slate-900 rounded-xl shadow-2xl disabled:opacity-20 disabled:pointer-events-none active:scale-98 transition-all text-center shrink-0 min-w-[140px]"
            >
              {isSubmitting ? 'Syncing...' : 'Continue'}
            </button>
          )}
        </div>

        {/* PROGRESS CIRCLES TRACKER */}
        <div className="flex space-x-3.5 mt-2">
          {[1, 2, 3, 4, 7].map((visibleStepNumber, idx) => {
            const isCircleActive = currentStep === visibleStepNumber;
            return (
              <div 
                key={idx} 
                className={`h-3 w-3 rounded-full transition-all duration-300 ${
                  isCircleActive ? "bg-white scale-125 shadow-xl" : "bg-white/30"
                }`} 
              />
            );
          })}
        </div>

        {/* STEP TEXT METRIC */}
        <div className="text-[10px] font-black uppercase tracking-widest text-white/60 mt-0.5">
          Step {[1, 2, 3, 4, 7].indexOf(currentStep) + 1} of 5
        </div>
      </footer>
    </div>
  );
};

export default OnboardingPage;